import MOCK_POLE_PLAN from "../mocks/polePlan.mock";
import {
    BoundPlanPolygon,
    BoundPolePlan,
    PlanServiceResource
} from "../models/PolePlanGeometry";
import { MapLocation } from "../services/LocationService";

type SlotAssignment<T> = {
    item: T;
    slotIndex: number;
};

/**
 * Associe dynamiquement les ressources FHIR chargées aux slots géométriques
 * du plan mocké. Le mock ne contient aucune référence FHIR.
 *
 * - Organization -> un des 5 slots de service ;
 * - Location -> un des 7 polygons du slot de son managingOrganization ;
 * - l'identifier FHIR est utilisé en priorité pour stabiliser la position ;
 * - l'id technique sert de repli ;
 * - les ressources dépassant la capacité du POC sont ignorées sans casser le rendu.
 */
export function bindPolePlanGeometry(
    services: PlanServiceResource[],
    locations: MapLocation[]
): BoundPolePlan {
    const serviceAssignments = assignToSlots(
        services,
        MOCK_POLE_PLAN.length,
        (service) => service.reference || service.id
    );
    const serviceBySlot = new Map(
        serviceAssignments.assignments.map((assignment) => [assignment.slotIndex, assignment.item])
    );
    const assignedServiceReferences = new Set(
        serviceAssignments.assignments.map((assignment) => assignment.item.reference)
    );
    const knownServiceReferences = new Set(services.map((service) => service.reference));
    const levels = getLevels(locations);
    const polygons: BoundPlanPolygon[] = [];

    let omittedLocationCount = locations.filter((location) =>
        !!location.organizationReference &&
        knownServiceReferences.has(location.organizationReference) &&
        !assignedServiceReferences.has(location.organizationReference)
    ).length;

    levels.forEach((level) => {
        MOCK_POLE_PLAN.forEach((serviceSlot, serviceSlotIndex) => {
            const service = serviceBySlot.get(serviceSlotIndex);
            const serviceLocations = service
                ? locations.filter((location) =>
                    location.organizationReference === service.reference &&
                    location.level === level
                )
                : [];
            const locationAssignments = assignToSlots(
                serviceLocations,
                serviceSlot.locationPolygons.length,
                getLocationStableKey
            );
            const locationBySlot = new Map(
                locationAssignments.assignments.map((assignment) => [
                    assignment.slotIndex,
                    assignment.item
                ])
            );

            omittedLocationCount += locationAssignments.omittedCount;

            serviceSlot.locationPolygons.forEach((polygon, locationSlotIndex) => {
                const location = locationBySlot.get(locationSlotIndex);

                polygons.push({
                    id: `${serviceSlot.id}-${level}-location-slot-${locationSlotIndex + 1}`,
                    level,
                    polygon,
                    serviceSlotIndex,
                    locationSlotIndex,
                    serviceId: service?.id,
                    serviceName: service?.name,
                    serviceReference: service?.reference,
                    locationId: location?.id,
                    locationName: location?.name,
                    isBoundLocation: !!location
                });
            });
        });
    });

    return {
        polygons,
        omittedServiceCount: serviceAssignments.omittedCount,
        omittedLocationCount
    };
}

export function getBoundLocationPolygons(
    plan: BoundPolePlan,
    serviceId?: string
): BoundPlanPolygon[] {
    return plan.polygons.filter((polygon) =>
        polygon.isBoundLocation &&
        (!serviceId || polygon.serviceId === serviceId)
    );
}

function getLevels(locations: MapLocation[]): string[] {
    const levels = Array.from(new Set(
        locations.map((location) => location.level || "0")
    ));

    if (levels.length === 0) return ["0"];

    return levels.sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
}

function getLocationStableKey(location: MapLocation): string {
    const identifier = [...location.identifiers].sort()[0];
    return identifier
        ? `identifier:${identifier}`
        : `id:${location.id}`;
}

function assignToSlots<T>(
    items: T[],
    slotCount: number,
    getStableKey: (item: T) => string
): { assignments: SlotAssignment<T>[]; omittedCount: number } {
    if (slotCount <= 0 || items.length === 0) {
        return {
            assignments: [],
            omittedCount: items.length
        };
    }

    const candidates = items
        .map((item) => {
            const key = getStableKey(item);
            const hash = stableHash(key);
            return {
                item,
                key,
                hash,
                preferredSlot: hash % slotCount
            };
        })
        .sort((left, right) =>
            left.preferredSlot - right.preferredSlot ||
            left.hash - right.hash ||
            left.key.localeCompare(right.key)
        );

    const usedSlots = new Set<number>();
    const assignments: SlotAssignment<T>[] = [];

    candidates.forEach((candidate) => {
        if (usedSlots.size >= slotCount) return;

        for (let offset = 0; offset < slotCount; offset += 1) {
            const slotIndex = (candidate.preferredSlot + offset) % slotCount;
            if (usedSlots.has(slotIndex)) continue;

            usedSlots.add(slotIndex);
            assignments.push({ item: candidate.item, slotIndex });
            return;
        }
    });

    return {
        assignments,
        omittedCount: Math.max(items.length - assignments.length, 0)
    };
}

function stableHash(value: string): number {
    let hash = 2166136261;

    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return hash >>> 0;
}
